// src/pages/RugbyMatchDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE } from "../api";
import { isAllowedPublicEdge } from "../utils/publicEdge";

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#f0fdf4",
  cardBg:     "#ffffff",
  pitchLine:  "#16a34a",
  pitchStripe:"#22c55e",
  tryZone:    "#15803d",
  accentDark: "#166534",
  accentLight:"#bbf7d0",
  textPri:    "#1e293b",
  textSub:    "#475569",
  textMut:    "#94a3b8",
  edgeGreen:  "#22c55e",
  edgeYellow: "#eab308",
  edgeRed:    "#ef4444",
};

/* ── SVG: rugby pitch background (fixed) ──── */
function PitchBg() {
  return (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "fixed", inset: 0, width: "100%", height: "100%",
        opacity: 0.12, pointerEvents: "none", zIndex: 0,
      }}
    >
      <rect x="40" y="60" width="720" height="280"
            fill="none" stroke={C.pitchLine} strokeWidth="3" />
      <rect x="40" y="60" width="60" height="280"
            fill={C.tryZone} opacity="0.15" />
      <rect x="700" y="60" width="60" height="280"
            fill={C.tryZone} opacity="0.15" />
      <line x1="100" y1="60" x2="100" y2="340"
            stroke={C.pitchLine} strokeWidth="3" />
      <line x1="700" y1="60" x2="700" y2="340"
            stroke={C.pitchLine} strokeWidth="3" />
      <line x1="400" y1="60" x2="400" y2="340"
            stroke={C.pitchLine} strokeWidth="2" />
      <line x1="180" y1="60" x2="180" y2="340"
            stroke={C.pitchStripe} strokeWidth="1.5" strokeDasharray="8,4" />
      <line x1="620" y1="60" x2="620" y2="340"
            stroke={C.pitchStripe} strokeWidth="1.5" strokeDasharray="8,4" />
      <line x1="280" y1="60" x2="280" y2="340"
            stroke={C.pitchStripe} strokeWidth="1" strokeDasharray="4,2" />
      <line x1="520" y1="60" x2="520" y2="340"
            stroke={C.pitchStripe} strokeWidth="1" strokeDasharray="4,2" />
      <circle cx="400" cy="200" r="40"
              fill="none" stroke={C.pitchLine} strokeWidth="1.5" />
    </svg>
  );
}

export default function RugbyMatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fixture, setFixture] = useState(null);
  const [odds, setOdds] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE}/api/fixtures/id/${id}/json`, { cache: "no-store" });
        if (!res.ok) throw new Error(`Fixture not found (${res.status})`);
        const data = await res.json();

        setFixture(data.fixture);
        setOdds(data.odds || []);
        setEdges(
          (data.best_edges || []).filter((edge) =>
            isAllowedPublicEdge(edge?.edge_pct ?? edge?.edge)
          )
        );
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatTimeUTC = (isoString) => {
    if (!isoString) return "—";
    const safe = /Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + "Z";
    try {
      const d = new Date(safe);
      return d.toLocaleString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
    } catch {
      return isoString;
    }
  };

  const edgeColor = (val) => {
    if (val >= 5) return C.edgeGreen;
    if (val >= 2) return C.edgeYellow;
    return C.textMut;
  };

  if (loading) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
        <PitchBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: 24 }}>
          <p style={{ color: C.textSub }}>Loading match details...</p>
        </div>
      </div>
    );
  }

  if (error || !fixture) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
        <PitchBg />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: 24 }}>
          <button
            onClick={() => navigate("/rugby")}
            style={{
              background: C.accentDark,
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              marginBottom: 20,
            }}
          >
            ← Back to Rugby
          </button>
          <p style={{ color: C.edgeRed }}>{error || "Match not found."}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
      <PitchBg />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: 24 }}>
        <button
          onClick={() => navigate("/rugby")}
          style={{
            background: C.accentDark,
            color: "#fff",
            border: "none",
            padding: "8px 16px",
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: "pointer",
            marginBottom: 20,
          }}
        >
          ← Back to Rugby
        </button>

        {/* Match header */}
        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 24,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <h2 style={{ color: C.accentDark, margin: 0 }}>🏉 {fixture.comp || "RUGBY"}</h2>
            <span
              style={{
                background: C.accentLight,
                color: C.accentDark,
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ID: {fixture.id}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 18 }}>
            {fixture.home_logo && (
              <img
                src={fixture.home_logo}
                alt={fixture.home_team}
                width={28}
                height={28}
                style={{ objectFit: "contain" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <strong style={{ color: C.textPri }}>{fixture.home_team}</strong>
            <span style={{ color: C.textMut }}>vs</span>
            {fixture.away_logo && (
              <img
                src={fixture.away_logo}
                alt={fixture.away_team}
                width={28}
                height={28}
                style={{ objectFit: "contain" }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            )}
            <span style={{ color: C.textPri }}>{fixture.away_team}</span>
          </div>
          <p style={{ color: C.textSub, marginTop: 8, marginBottom: 0 }}>
            Kickoff: {formatTimeUTC(fixture.kickoff_utc)}
          </p>
        </div>

        {/* Show availability only when this fixture has no provider odds. */}
        {odds.length === 0 && <div
          style={{
            background: C.accentLight,
            border: `2px solid ${C.pitchLine}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          <p style={{ color: C.accentDark, fontWeight: 600, margin: 0 }}>
            Rugby odds are not available from the provider for this match yet.
          </p>
        </div>}

        {/* Odds section */}
        {odds.length > 0 && (
          <div
            style={{
              background: C.cardBg,
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <h3 style={{ color: C.accentDark, marginBottom: 16 }}>Available Odds</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.pitchLine}` }}>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Bookmaker
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Market
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {odds.map((o, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                    <td style={{ padding: "10px 0", color: C.textPri }}>{o.bookmaker}</td>
                    <td style={{ padding: "10px 0", color: C.textPri }}>{o.market}</td>
                    <td align="right" style={{ padding: "10px 0", color: C.textPri, fontWeight: 600 }}>
                      {o.price}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edges section */}
        {edges.length > 0 && (
          <div
            style={{
              background: C.cardBg,
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <h3 style={{ color: C.accentDark, marginBottom: 16 }}>Detected Edges</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.pitchLine}` }}>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Bookmaker
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Market
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Price
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Model Prob (%)
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Edge (%)
                  </th>
                </tr>
              </thead>
              <tbody>
                {edges.map((e, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${C.accentLight}` }}>
                    <td style={{ padding: "10px 0", color: C.textPri }}>{e.bookmaker}</td>
                    <td style={{ padding: "10px 0", color: C.textPri }}>{e.market}</td>
                    <td align="right" style={{ padding: "10px 0", color: C.textPri, fontWeight: 600 }}>
                      {e.price}
                    </td>
                    <td align="right" style={{ padding: "10px 0", color: C.textSub }}>
                      {e.model_prob ? (e.model_prob * 100).toFixed(1) : "—"}
                    </td>
                    <td
                      align="right"
                      style={{
                        padding: "10px 0",
                        fontWeight: 700,
                        color: edgeColor(e.edge_pct || 0),
                      }}
                    >
                      {e.edge_pct ? `+${e.edge_pct.toFixed(2)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {odds.length === 0 && edges.length === 0 && (
          <p style={{ color: C.textMut, textAlign: "center", marginTop: 40 }}>
            No odds or edges available yet. Check back once the Rugby API subscription is active.
          </p>
        )}
      </div>
    </div>
  );
}
